import { Component, DestroyRef, EventEmitter, OnInit, Output, ViewChild, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs';
import { NursingAuditService } from 'src/app/services/nursing-audit.service';
import { TableComponent} from 'src/app/peer-to-peer/table/table.component';
import { UrcComponent } from "src/app/variance/urc/urc.component";
import { Table } from 'primeng/table';
import { ThemeColorService } from 'src/app/services/theme-color.service';
import { ThemePreferenceService } from 'src/app/services/theme-preference.service';

interface RiskPercentViewModel {
  [key: string]: unknown;
  riskPercent?: unknown;
  riskPercentValue: number | null;
  riskPercentDisplay: string;
  riskPointsValue: number | null;
  riskPointsDisplay: string;
  scoreDisplay: string;
}

type RiskScoreDisplayField = 'riskPercentDisplay' | 'riskPointsDisplay' | 'scoreDisplay';

interface RiskScoreColumn {
  field: string;
  header: string;
  displayField?: RiskScoreDisplayField;
}

@Component({
    selector: 'app-risk-score-chart',
    templateUrl: './risk-score-chart.component.html',
    styleUrls: ['./risk-score-chart.component.css'],
    standalone: false
})

export class RiskScoreChartComponent implements OnInit {
  private nursingAuditService = inject(NursingAuditService);
  private readonly themeColors = inject(ThemeColorService);
  private readonly themePreferenceService = inject(ThemePreferenceService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() data: any;
  private _highRiskResolutions: RiskPercentViewModel[] = [];
  @Input()
  set highRiskResolutions(rows: unknown) {
    this._highRiskResolutions = this.prepareRiskPercentRows(rows);
  }
  get highRiskResolutions(): RiskPercentViewModel[] {
    return this._highRiskResolutions;
  }
  highRiskResoCols: RiskScoreColumn[] = [];
  private _highRiskP2P: RiskPercentViewModel[] = [];
  @Input()
  set highRiskP2P(rows: unknown) {
    this._highRiskP2P = this.prepareRiskPercentRows(rows);
  }
  get highRiskP2P(): RiskPercentViewModel[] {
    return this._highRiskP2P;
  }
  highRiskP2PCols: RiskScoreColumn[] = [];
  @Input() row: any;
  @Output() shareIndvActiv = new EventEmitter();
  @Input() startWithPieChart = false;
  label = '';
  chartOptions: any = {};
  iaFilter: any = {};
  peerFilter: any = {};
  selectedDays: any = 90
  type: 'pie' | 'line' = 'line';
  get isPieChart() { return this.type === 'pie'; }
  @Input() location:any;
  @Input() adminGroup:any;
  @Input() lanId:any;
  @Input() deId=false;
  p2pRow:any;
  p2pFilter:any;
  URCFilter:any;
  showP2PTable = false;
  showURCTable = false;
  loading = false;
  @ViewChild('highRiskP2PTable') highRiskP2PTable?: Table;
  @ViewChild('highRiskResoTable') highRiskResoTable?: Table;
  @ViewChild('p2pFilteredTable') p2pFilteredTable?: TableComponent;
  @ViewChild('varianceComponent') resolutionComp?: UrcComponent;

  constructor() {
    this.themePreferenceService.preference$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.chartOptions = this.type === 'pie'
          ? this.createCurrentRiskOptions()
          : this.createRiskTrendOptions();
      });
  }

  ngOnInit(): void {
    if (this.startWithPieChart) {
      const pieData = this.data && typeof this.data === 'object'
        ? this.data as Record<string, unknown>
        : {};
      this.showCurrentRiskChart(pieData);
    } else {
      this.type = 'line';
    }
    this.highRiskResoCols = [
      { field: "medGroup", header: "Med. Group" },
      { field: "resolutionCode", header: "Variances" },
      { field: "count", header: "Count" },
      {field: "riskPointsValue", header: "Risk points", displayField: "riskPointsDisplay"},
      { field: "riskPercentValue", header: "Score Contribution (+/- 0.01%)", displayField: "riskPercentDisplay" }
    ];
    this.highRiskP2PCols = [
      { field: "medGroup", header: "Med. Group" },
      { field: "shiftworked", header: "Shift" },
      { field: "auditType", header: "Peer Audit" },
      { field: "score", header: "Peer Score", displayField: "scoreDisplay" },
      {field: "riskPointsValue", header: "Risk points", displayField: "riskPointsDisplay"},
      { field: "riskPercentValue", header: "Score Contribution (+/- 0.01%)", displayField: "riskPercentDisplay" }
    ];
  }

  loadRiskScoreChart(row: any, showPieChart = false, resetTableSort = true){
    this.loading=true
    if (resetTableSort) {
      this.resetRiskTableSorts();
    }
    this.reset();
    this.location = row['locationGroupingString']
    this.adminGroup = row['adminGroup']
    this.lanId = row['lanId']
    this.row = row
    // Set URCFilter for Individual Activity dialog
    this.URCFilter = {
      loc: this.location,
      adminType: this.adminGroup,
      employeeId: this.lanId
    };
    this.nursingAuditService.loadRiskScoringHistory(row).subscribe((response) => {
      const responseList = Array.isArray(response) ? response : [];
      const riskData = responseList[0];
      this.highRiskResolutions = responseList[1];
      this.highRiskP2P = responseList[2];
      if (showPieChart) {
        const pieData = riskData && typeof riskData === 'object' ? (riskData as Record<string, unknown>) : {};
        this.showCurrentRiskChart(pieData);
        this.loading = false;
      } else {
        this.loadRiskScoreTrend(row);
      }
    });
  }

  prepareRiskPercentRows(rows: unknown): RiskPercentViewModel[] {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows
      .map((row: Record<string, unknown>, index: number) => {
        const riskPercentValue = this.parseRiskPercent(row?.riskPercent);
        const riskPointsValue = this.parseRiskPoints(row?.riskPoints);
        const viewModel: RiskPercentViewModel = {
          ...row,
          riskPercentValue,
          riskPercentDisplay: this.formatRiskPercentValue(row?.riskPercent, riskPercentValue),
          riskPointsValue,
          riskPointsDisplay: this.formatRiskPoints(row?.riskPoints),
          scoreDisplay: this.formatPeerScore(row?.score)
        };

        return { viewModel, index };
      })
      .filter(({ viewModel }) => {
        return viewModel.riskPercentValue === null
          || this.roundRiskPercentValue(viewModel.riskPercentValue) !== 0;
      })
      .sort((first, second) => {
        if (first.viewModel.riskPercentValue === null && second.viewModel.riskPercentValue === null) {
          return first.index - second.index;
        }
        if (first.viewModel.riskPercentValue === null) {
          return 1;
        }
        if (second.viewModel.riskPercentValue === null) {
          return -1;
        }

        return second.viewModel.riskPercentValue - first.viewModel.riskPercentValue
          || first.index - second.index;
      })
      .map(({ viewModel }) => viewModel);
  }

  formatRiskPercent(value: unknown): string {
    return this.formatRiskPercentValue(value, this.parseRiskPercent(value));
  }

  formatRiskPoints(value: unknown): string {
    return this.formatCompactDecimal(value);
  }

  formatPeerScore(value: unknown): string {
    return this.formatCompactDecimal(value);
  }

  private formatCompactDecimal(value: unknown): string {
    if (typeof value === 'number' && Number.isNaN(value)) {
      return '';
    }

    const displayValue = `${value ?? ''}`.trim();

    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(displayValue)) {
      return displayValue;
    }

    return displayValue
      .replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0*$/, '');
  }

  private formatRiskPercentValue(originalValue: unknown, numericValue: number | null): string {
    return numericValue === null
      ? `${originalValue ?? ''}`
      : `${this.formatCompactDecimal(this.roundRiskPercentValue(numericValue).toFixed(2))}%`;
  }

  private roundRiskPercentValue(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private parseRiskPercent(value: unknown): number | null {
    return this.parseDecimal(value, true);
  }

  private parseRiskPoints(value: unknown): number | null {
    return this.parseDecimal(value, false);
  }

  private parseDecimal(value: unknown, allowPercentSuffix: boolean): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    const normalizedValue = allowPercentSuffix && trimmedValue.endsWith('%')
      ? trimmedValue.slice(0, -1).trim()
      : trimmedValue;
    const decimalPattern = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

    if (!normalizedValue || !decimalPattern.test(normalizedValue)) {
      return null;
    }

    const numericValue = Number(normalizedValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  showCurrentRiskChart(pieData: Record<string, unknown>){
    this.data = pieData
    this.label = 'Current Risk'
    this.type = 'pie'
    this.chartOptions = this.createCurrentRiskOptions();
  }

  private createCurrentRiskOptions(): any {
    return {
      plugins: {
        legend: {
          display: true,
          labels: {
            color: this.themeColors.resolve('--text-primary')
          }
        },
        title: {
          display: true,
          text: 'Current User Score',
          color: this.themeColors.resolve('--text-primary')
        }
      }
    };
  }
  loadRiskScoreTrend(row: any){
    this.type = 'line'
    this.label = 'Risk History'
    this.data = {}
    this.nursingAuditService.loadRiskTrending(row).subscribe((response) => {
      this.loading=false;
      this.data = (response as any)[0]
      this.chartOptions = this.createRiskTrendOptions();
    })
  }

  private createRiskTrendOptions(): any {
    return {
        plugins: {
          legend: {
            display: true,
            labels: {
              color: this.themeColors.resolve('--text-primary')
            }
          },
          title: {
            display: true,
            text: 'Historical User Score',
            color: this.themeColors.resolve('--text-primary')
          }
        },
        scales: {
          x: {
            ticks: {
              color: this.themeColors.resolve('--text-primary')
            },
            grid: {
              color: this.themeColors.resolve('--chart-grid-line-faint')
            }
          },
          y: {
            ticks: {
              color: this.themeColors.resolve('--text-primary')
            },
            grid: {
              color: this.themeColors.resolve('--chart-grid-line-faint')
            }
          }
        }
      };
  }

  chartViewSwitch(showPieChart: boolean): void {
    this.loading = true;
    if (showPieChart) {
      this.loadRiskScoreChart(this.row, true, false);
    } else {
      this.loadRiskScoreTrend(this.row);
    }
  }

  private resetRiskTableSorts(): void {
    this.highRiskP2PTable?.reset?.();
    this.highRiskResoTable?.reset?.();
  }

  reset(){
    this.type = 'line';
    this.data = {};
  }

  openP2PFilteredTable(row: any){
    this.peerFilter = {
                    ...(this.location ? { locationGroup: [this.location] } : {}),
                    "adminGroup":this.adminGroup,
                    "p2p_type":row['auditType'],
                    "shiftworked":row['shiftworked'],
                    "medGroupName":row['medGroup']
                  };
    this.showP2PTable=!this.showP2PTable
  }

  openURCFilteredTable(row: any){
    // Set URCFilter with row-specific filters for Variance Table click
    this.URCFilter = {
      ia: true,
      showMultiAdmins: true,
      duration: 90,
      employeeId: this.lanId,
      locationGrouping: this.location,
      adminType: this.adminGroup,
      resolution: row['resolutionCode'],
      medGroup: row['medGroup'],
    };
    this.showURCTable = true;
  }

  getFilterPillsValues(newFilters: any){
    const updatedFilters = { ...(newFilters || {}) };
    if(Array.isArray(updatedFilters.medgroup) && updatedFilters.medgroup[0]){
      updatedFilters.medGroupName = updatedFilters.medgroup;
    }
    if(this.showURCTable){
      this.iaFilter = { ...updatedFilters };
    }
    if(this.showP2PTable){
      this.peerFilter = { ...updatedFilters };
    }
  }
  removePeerFilter(filter: any): void {
    const updatedFilter = { ...(filter || {}) };
    this.peerFilter = { ...updatedFilter };
    this.p2pFilteredTable?.removePeertoPeerFilters(updatedFilter);
  }

  removeIaFilter(filter: any): void {
    const updatedFilter = { ...(filter || {}) };
    this.iaFilter = { ...updatedFilter };
    this.resolutionComp?.removeVarianceFilters(updatedFilter, this.selectedDays);
  }

  shareIndividualActivity(items: any): void {
    this.shareIndvActiv.emit(items);
  }

  ColorAssignment(backgroundColor: any){
    return {
      'background-color': backgroundColor, 
      'font-weight': 'bolder', 
      'cursor': 'pointer',
      'color': 'rgb(var(--text-on-chart-dark-surface))'
    }
  }

  ColorAssignmentP2P(backgroundColor: any){
    const usesLightSurface = typeof backgroundColor === 'string'
      && backgroundColor.trim().toUpperCase().startsWith('#FFF');

    return {
      'background-color': backgroundColor,
      'font-weight': 'bolder',
      'cursor': 'pointer',
      'color': usesLightSurface
        ? 'rgb(var(--text-on-chart-light-surface))'
        : 'rgb(var(--text-on-chart-dark-surface))'
    }
  }
}
